// TODO: replace this completely with RemoteUserWords,
//       may require disabling flashcards for users not signed up

import Backbone from "backbone";
import * as _ from "underscore";

import * as currentDate from "currentDate";
import UserModel from "models/UserModel";
import UserWordModel from "models/UserWordModel";
import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";
import * as spacedRepetition from "shared/spacedRepetition";

interface Counts {
  total: number;
  justStarted: number;
  gettingThere: number;
  mastered: number;
  scheduled: number;
  averagePerDay: number;
  scheduledForAveraging: number;
  notStarted: number;
  readyToTest: number;
}

export default class UserWordsCollection extends Backbone.Collection<UserWordModel> {
  url = "/api/userWords";
  model = UserWordModel;

  user: UserModel;
  groupsUpdatedTimer: number;
  date: Date;
  updateNullCardsCallbacks: Function[];
  _counts: Counts;
  _groups: {
    readyToTest: Backbone.Collection;
    notStarted: Backbone.Collection;
    justStarted: Backbone.Collection;
    gettingThere: Backbone.Collection;
    mastered: Backbone.Collection;
  };
  fetchedGroups: {
    readyToTest?: boolean;
    notStarted?: boolean;
    justStarted?: boolean;
    gettingThere?: boolean;
    mastered?: boolean;
    scheduled?: number;
  };

  // XXX What is all this null cards stuff??
  updatingNullCards: boolean;

  triggerGroupsUpdated() {
    const that = this;

    clearTimeout(this.groupsUpdatedTimer);
    this.groupsUpdatedTimer = setTimeout(function () {
      that.trigger("groupsUpdated");
    }, 0);
  }

  constructor(models, options) {
    super(models, options);

    this.user = options.user;

    currentDate.get().then((date: Date) => {
      this.date = new Date(date.getTime() + 1000);
    });

    this.resetGroups();

    this.updateNullCardsCallbacks = [];

    this.user.on("change:currentLearningLanguage", async () => {
      if (this._counts) {
        this.resetGroups();

        await this.getGroupCounts(true);

        this.triggerGroupsUpdated();
        this.trigger("groupsReCreated");
      }
    });
    this.on("add", (model, collection, innerOptions) => {
      const groups = spacedRepetition.calcGroups(model, this.date);
      _.each(groups, (groupName) => {
        if (groupName === "readyToTest") {
          this._groups[groupName].add(model, { at: 0 });
        } else {
          this._groups[groupName].add(model);
        }
      });

      if (this._counts) {
        if (!innerOptions.fetchingGroup) {
          this._counts.averagePerDay += model.perDayAverage(this.date);
        }

        _.each(groups, (groupName) => {
          if (!innerOptions.fetchingGroup && this._counts) {
            this._counts[groupName]++;
          }
        });

        this.updateNullCards();
        this.triggerGroupsUpdated();
      }
    });
    this.on("remove", (model) => {
      if (this._counts) {
        this._counts.averagePerDay -= model.perDayAverage(this.date);
        this.updateNullCards();

        _.each(_.keys(this._groups), (groupName) => {
          if (this._groups[groupName].contains(model)) {
            this._groups[groupName].remove(model);
            if (this._counts) {
              this._counts[groupName]--;
            }
          }
        });
        this.triggerGroupsUpdated();
      }
    });
    this.on("change", async (model) => {
      if (this._counts) {
        // Note: the average shouldn't normally change during a session,
        //       it should change the following day instead
        const avgChange = model.perDayAverageChange(this.date);
        this._counts.averagePerDay += avgChange;

        // the average per day is updated in reportRecallEase
        currentDate.get().then(() => {
          const groups = spacedRepetition.calcGroups(model, this.date);

          _.each(_.keys(this._groups), (groupName) => {
            if (
              !_.contains(groups, groupName) &&
              this._groups[groupName].contains(model)
            ) {
              this._groups[groupName].remove(model);
              if (this._counts) {
                this._counts[groupName]--;
              }
            } else if (
              _.contains(groups, groupName) &&
              !this._groups[groupName].contains(model)
            ) {
              this._groups[groupName].add(model);
              if (this._counts) {
                this._counts[groupName]++;
              }
            }
          });

          this.triggerGroupsUpdated();
        });
      }
    });
  }

  resetGroups() {
    this.reset([]);

    this.fetchedGroups = {};
    this._groups = {
      readyToTest: new Backbone.Collection(),
      notStarted: new Backbone.Collection(),

      justStarted: new Backbone.Collection(),
      gettingThere: new Backbone.Collection(),
      mastered: new Backbone.Collection(),
    };
    this._counts = null;
  }

  findByWord(language, word) {
    return this.find(function (userWord) {
      return (
        userWord.get("language") === language && userWord.get("word") === word
      );
    });
  }

  updateDailyLimit(newLimit) {
    spacedRepetition.wordsPerDay(newLimit);
    this.updateNullCards();
    this.triggerGroupsUpdated();
  }

  increaseDailyLimit(amount) {
    // TODO: better solution for this
    spacedRepetition.wordsPerDay(this.averagePerDay() + amount);
    this.updateNullCards();
    this.triggerGroupsUpdated();
  }

  // total words to test - scheduled + not started up to daily limit
  totalToTest() {
    // TODO: this function is called way too often - investigate
    const extraWordsToTest = Math.max(
      0,
      Math.min(
        Math.round(
          (spacedRepetition.wordsPerDay() - this._counts.averagePerDay) /
            spacedRepetition.NEW_WORD_AVERAGE
        ),
        this._counts.notStarted
      )
    );

    let readyToTestCards = 0;
    if (this._groups && this.fetchedGroups.scheduled) {
      this._groups.readyToTest.each(function (card) {
        if (card.has("nextDate")) {
          readyToTestCards++;
        }
      });
    } else if (this._counts) {
      readyToTestCards = this._counts.scheduled;
    }

    return readyToTestCards + extraWordsToTest;
  }

  averagePerDay() {
    return this._counts.averagePerDay;
  }

  doAfterNullCardsUpdated(callback) {
    if (!this.updatingNullCards) {
      callback();
    } else {
      this.updateNullCardsCallbacks.push(callback);
    }
  }

  updateNullCards() {}

  _updateNullCards() {
    let delta = Math.round(
      this.totalToTest() - this._groups.readyToTest.length
    );

    if (delta === 0) {
      return;
    }

    if (delta < 0) {
      // remove nulls
      let toRemove = this._groups.readyToTest.filter(function (word) {
        return !word.has("word");
      });

      toRemove = _.shuffle(toRemove);

      while (delta < 0 && toRemove.length > 0) {
        const item = toRemove.pop();
        this._groups.readyToTest.remove(item);
        item.destroy();
        delta++;
      }
    }

    while (delta > 0) {
      this._groups.readyToTest.add(new UserWordModel(), {
        at: this._groups.readyToTest.length,
      });
      delta--;
    }

    this.triggerGroupsUpdated();
  }

  async getGroups(groups) {
    const result: Partial<typeof this._groups> = {};

    for (const group of groups) {
      const groupData = await this.getGroup(group);
      result[group] = groupData;
    }

    return result;
  }

  async getBookWordsToTest(bookIDs) {
    const data = {
      bookIDs: bookIDs.join("|"),
      wordGroup: "readyToTest",
      language: this.user.get("currentLearningLanguage"),
    };
    let resultData = await this.fetch({
      remove: false,
      data: data,
    });

    if (resultData.length < 5) {
      // Fetch all words, no matter if they are ready to test or not
      resultData = await this.fetch({
        remove: false,
        data: {
          ...data,
          wordGroup: undefined,
        },
      });
    }

    const bookWordsCollection = new Backbone.Collection();

    for (const word of resultData) {
      bookWordsCollection.add(new UserWordModel(word));
    }

    return bookWordsCollection;
  }

  async getGroup(group): Promise<Backbone.Collection> {
    const that = this;

    // Always force fetching for consistency, so skip this
    // if (this.fetchedGroups[group]) {
    //   return this._groups[group];
    // }

    const data: {
      language: string;
      timezoneOffset: number;
      wordGroup?: string;
      limit?: number;
      timestamp?: number;
    } = {
      language: that.user.get("currentLearningLanguage"),
      timezoneOffset: dateUtils.getTimezoneOffset(),
    };
    if (group !== "all") {
      data.wordGroup = group;
    }

    if (group === "notStarted") {
      delete that._groups.notStarted.comparator;
      that._groups.notStarted.off();
    }
    if (group === "readyToTest") {
      data.limit = 60;
      delete that._groups.readyToTest.comparator;
      that._groups.readyToTest.off();
    }
    if (group === "notStarted" || group === "all") {
      that._groups.notStarted.off();
      that._groups.readyToTest.off();
    }

    if (group === "reviewedToday") {
      data.wordGroup = "reviewedSince";
      data.limit = 9999;

      const date = await currentDate.getSnapped(0);
      data.timestamp = Math.round(date.getTime() / 1000);
    }

    const result: Backbone.Collection = await new Promise((resolve) => {
      this.fetch({
        remove: false,
        // @ts-ignore
        fetchingGroup: true,
        data,
        success: function () {
          if (
            !data.limit &&
            !_.contains(["readyToTest", "reviewedToday"], group)
          ) {
            that.fetchedGroups[group] = true;
          }

          if (_.contains(["notStarted", "readyToTest", "scheduled"], group)) {
            that.updateNullCards();
          }

          if (group === "notStarted" || group === "readyToTest") {
            that._groups.readyToTest.comparator =
              that._groups.notStarted.comparator = function (userWord) {
                const favBonus = userWord.attributes.favorite ? 10000000 : 0;

                let weight = userWord.attributes.frequency || 0.5;
                if ("interval" in userWord.attributes) {
                  weight *= 2;
                }

                return -favBonus - weight;
              };
            that._groups.notStarted.sort();
            that._groups.notStarted.on(
              "change:favorite change:frequency",
              function () {
                that._groups.notStarted.sort();
              }
            );
            that._groups.readyToTest.sort();
            that._groups.readyToTest.on(
              "change:favorite change:frequency",
              function () {
                that._groups.readyToTest.sort();
              }
            );
          }

          if (group === "all") {
            resolve(that);
          } else {
            resolve(that._groups[group]);
          }
        },
      });
    });

    return result;
  }

  async getGroupCounts(forceFetch: boolean = false) {
    if (!this._counts || forceFetch) {
      const data = (
        await readlangClient.request("GET", "/api/userWordCounts", {
          language: this.user.get("currentLearningLanguage"),
          timezoneOffset: dateUtils.getTimezoneOffset(),
        })
      ).data;
      this._counts = data;
      this.trigger("groupsUpdated");
    }

    if (this._groups && this.fetchedGroups.readyToTest) {
      this._counts.readyToTest = this._groups.readyToTest.length;
    } else {
      this._counts.readyToTest = Math.max(
        this._counts.scheduled,
        Math.min(
          this._counts.scheduled + this._counts.notStarted,
          30 /* TODO: make DRY */
        )
      );
    }

    return this._counts;
  }

  nextTestCard(callback) {
    const that = this;

    // TODO: clean up
    this.doAfterNullCardsUpdated(function () {
      if (that.totalToTest() === 0) {
        callback(null);
        return;
      }

      let word = that._groups.readyToTest.last();
      if (word && !word.has("nextDate")) {
        if (that._groups.notStarted.length === 0) {
          that._groups.readyToTest.remove(word);
          word.destroy();
          console.error("null card in queue but none in notStarted");
          that.nextTestCard(callback);
          return;
        } else {
          word = that._groups.notStarted.at(0);
        }
      }

      callback(word);
    });
  }

  async reportRecallEase(currentWord, ease) {
    const readyToTest = this._groups.readyToTest;

    if (!(readyToTest.last() && readyToTest.last().has("word"))) {
      readyToTest.remove(readyToTest.last());
    }

    let cardsToWait;

    const date = await currentDate.get();

    if (
      // lastRecallEase > 1 means that we are reporting on an active recall attempt
      currentWord.get("lastRecallEase") > 1 ||
      this.user.getFlashcardMode() === "passive" ||
      ease >= 5
    ) {
      cardsToWait = spacedRepetition.wordRecalled(currentWord, ease, date);
    } else {
      // lastRecallEase <= 1 means that we are reporting on a passive recall attempt,
      // and we want the card to be scheduled to be re-surfaced immediately
      cardsToWait = spacedRepetition.wordRecalled(
        currentWord,
        ease,
        date,
        1,
        0
      );
    }

    if (cardsToWait === -1) {
      readyToTest.remove(currentWord);
    } else {
      readyToTest.remove(currentWord);
      readyToTest.add(currentWord, {
        at: Math.max(0, readyToTest.length - cardsToWait),
      });
    }

    this.updateNullCards();

    return cardsToWait;
  }

  addWord(userWord, callback) {
    const that = this;
    userWord.sync("create", userWord, {
      success: function (result) {
        if (!result.success) {
          console.error("create userWord failed");
          return;
        }
        if (result.frequency) {
          userWord.set("frequency", result.frequency);
        }

        if (
          userWord.get("language") !== that.user.get("currentLearningLanguage")
        ) {
          return;
        }

        if (result.updated) {
          const existing = that.get(result.updated);
          if (existing) {
            // not destroying since that will delete on server:
            existing.trigger("destroy", existing, existing.collection);
          }

          new UserWordModel({ _id: result.updated }).fetch({
            success: function (fetchedWord) {
              if (callback) {
                callback(fetchedWord);
              }
            },
          });
        } else {
          if (!userWord.has("_id")) {
            userWord.set("_id", result.updated || result.upserted);
          }
          that.add(userWord);
          if (callback) {
            callback(userWord);
          }
        }
      },
    });
  }

  /** This must be called after fetching from the server with getGroups() */
  async completedToday() {
    const that = this;
    const result: any = new Backbone.Collection();

    result.deltas = {
      notStarted: 0,
      justStarted: 0,
      gettingThere: 0,
      mastered: 0,
    };
    const originalCounts = {
      notStarted: 0,
      justStarted: 0,
      gettingThere: 0,
      mastered: 0,
    };
    const newCounts = _.clone(originalCounts);

    const date = await currentDate.getSnapped(this.user.get("updateHour"));
    const lowerBound = await date.getTime();
    const upperBound = lowerBound + spacedRepetition.DAY;

    that.each(function (userWord) {
      const nextDate = userWord.get("nextDate"),
        interval = userWord.get("interval");

      if (nextDate && interval) {
        const previousDate =
          new Date(dateUtils.parse(nextDate)).getTime() - interval;
        if (previousDate > lowerBound && previousDate <= upperBound) {
          result.push(userWord);
          originalCounts[
            spacedRepetition.getIntervalName(userWord.get("previousInterval"))
          ]++;
          newCounts[
            spacedRepetition.getIntervalName(userWord.get("interval"))
          ]++;
        }
      }
    });

    _.each(originalCounts, function (originalCount, name) {
      result.deltas[name] = newCounts[name] - originalCount;
    });

    return result;
  }

  // Used for debugging
  // Moves all scheduled dates back by
  fastForward(days) {
    this.each(function (userWord) {
      if (userWord.has("nextDate")) {
        userWord.set(
          "nextDate",
          new Date(
            new Date(dateUtils.parse(userWord.get("nextDate"))).getTime() -
              days * 24 * 3600 * 1000
          ).toISOString()
        );
        userWord.save();
      }
      if (userWord.has("previousIntervalDate")) {
        userWord.set(
          "previousIntervalDate",
          new Date(
            new Date(
              dateUtils.parse(userWord.get("previousIntervalDate"))
            ).getTime() -
              days * 24 * 3600 * 1000
          ).toISOString()
        );
        userWord.save();
      }
    });
  }
}
